/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file buildInstanceFunctions internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\build-instance-functions
 */

import { Dispatch } from "react";
import { IAsyncFunction, IAction } from "../";
import { buildStateUpdater } from "./build-state-updater";
import { buildCallbackExecutor } from "./build-callback-executor";
import { buildAsyncExecutor } from "./build-async-executor";
import { buildAsyncIgnorer } from "./build-async-ignorer";
import {
	AsyncFinishersRef,
	ExecutorRef,
	CallbackExecutorRef,
	IgnorerRef,
	NextIdRef,
	StateUpdaterRef,
} from "../types";
import { IErrorHandler, ISuccessHandler } from "../../types";

/**
 * Build instance functions needed by the tracker and store them in refs
 */
export function buildInstanceFunctions<TAsync extends IAsyncFunction>(
	asyncOperation: TAsync,
	dispatch: Dispatch<IAction>,
	asyncFinishers: AsyncFinishersRef,
	execute: ExecutorRef<TAsync>,
	executeCallbacks: CallbackExecutorRef,
	ignore: IgnorerRef,
	nextId: NextIdRef,
	updateState: StateUpdaterRef,
	onFailure?: IErrorHandler<TAsync>,
	onSuccess?: ISuccessHandler<TAsync>,
): void {
	// Build all the workhorse functions
	updateState.current = buildStateUpdater(dispatch);
	executeCallbacks.current = buildCallbackExecutor(onFailure, onSuccess);
	execute.current = buildAsyncExecutor(
		asyncOperation,
		asyncFinishers,
		executeCallbacks,
		nextId,
		updateState,
	) as any;
	ignore.current = buildAsyncIgnorer(asyncFinishers, updateState);
}
